import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const ContentStructureViewResult = ({result}) => {
  const getQualityColor = (quality) => {
    if (typeof quality !== 'string') return 'bg-base-200 text-base-content';
    
    switch(quality.toLowerCase()) {
      case 'excellent':
      case 'good':
        return 'bg-success/20 text-success';
      case 'fair':
      case 'logical':
        return 'bg-warning/20 text-warning';
      case 'poor':
      case 'illogical':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const renderObjectData = (data) => {
    if (!data || typeof data !== 'object') return null;
    
    return Object.entries(data).map(([key, value]) => {
      // Skip rendering if value is null or undefined
      if (value == null) return null;

      // Handle nested objects
      if (typeof value === 'object' && !Array.isArray(value)) {
        return (
          <div key={key} className="p-3 bg-base-200 rounded-lg">
            <span className="text-sm text-base-content/70 capitalize">
              {key.replace(/_/g, ' ')}
            </span>
            <div className="mt-2 space-y-2">
              {renderObjectData(value)}
            </div>
          </div>
        );
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return (
          <div key={key} className="p-3 bg-base-200 rounded-lg">
            <span className="text-sm text-base-content/70 capitalize">
              {key.replace(/_/g, ' ')}
            </span>
            <div className="mt-2 space-y-2">
              {value.length > 0 ? value.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-base-300 rounded-lg">
                  <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                  <span className="text-sm">{item}</span>
                </div>
              )) : (
                <span className="text-sm text-base-content/50">No items found</span>
              )}
            </div>
          </div>
        );
      }

      // Handle simple key-value pairs
      return (
        <div key={key} className="flex justify-between items-center">
          <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
          {typeof value === 'boolean' ? (
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              value ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
            }`}>
              {value ? 'Yes' : 'No'}
            </span>
          ) : (
            <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(value)}`}>
              {value.toString()}
            </span>
          )}
        </div>
      );
    });
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Content Structure Section */}
        <div className="space-y-4">
          {/* Structure Status Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.SquaresFour size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Content Structure</h3>
              </div>

              <div className="space-y-3">
                {result?.analysis?.content_status && (
                  renderObjectData(result.analysis.content_status)
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Validation Section */}
        <div className="space-y-4">
          {/* Validation Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.CheckCircle size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Validation</h3>
              </div>

              <div className="space-y-3">
                {result?.analysis?.validation && (
                  renderObjectData(result.analysis.validation)
                )}

                {/* Analysis Timestamp */}
                {result?.metadata?.analysis_timestamp && (
                  <div className="p-3 bg-base-200 rounded-lg text-sm">
                    <span className="text-base-content/70">Last Updated:</span>
                    <span className="ml-2">
                      {new Date(result.metadata.analysis_timestamp).toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Confidence Score */}
                {result?.metadata?.confidence_score != null && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Confidence</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round(result.metadata.confidence_score * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-base-300 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-success"
                        style={{width: `${result.metadata.confidence_score * 100}%`}}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentStructureViewResult;